import { useEffect, useMemo, useRef, useState } from "react"
import CodeMirror from "@uiw/react-codemirror"
import type { EditorView as EditorViewType } from "@codemirror/view"
import { EditorView } from "@codemirror/view"
import {
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  ShieldCheck,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { WorkflowGraphPanel } from "@/components/domain/workflow-graph"
import { cn } from "@/lib/cn"

type WorkflowScope = "skill"

type Props = {
  value: string
  onChange: (next: string) => void
  savedValue?: string
  toolKeys?: string[]
  placeholder?: string
  minHeight?: string
  readOnly?: boolean
  onSave?: () => void
  saveLabel?: string
  footerHint?: string
  outlineSubtitle?: string
  toolHeading?: string
  workflowScope?: WorkflowScope
  skillId?: number
  previewUrl?: string
}

type PreviewWarning = {
  code: string
  severity: "error" | "warning" | "info"
  message: string
  path?: string
  value?: string
  suggestions?: string[]
}

type WorkflowPreview = {
  ok: boolean
  errors: string[]
  warnings: PreviewWarning[]
  outline: WorkflowOutline
}

type WorkflowOutline = {
  engine?: string
  version?: number | string | null
  purpose?: string
  trigger?: Record<string, unknown>
  scope?: WorkflowScope | string
  global_tools?: string[]
  all_tools?: string[]
  blocked_interrupts?: string[]
  endpoint_allowlists?: {
    global?: string[]
    phases?: Record<string, string[]>
  }
  endpoint_samples?: WorkflowEndpointSample[]
  runtime?: WorkflowRuntime
  testing?: WorkflowTesting
  start_actions?: WorkflowAction[]
  interrupts?: WorkflowInterrupt[]
  cards?: WorkflowCard[]
}

type WorkflowEndpointSample = {
  id: number | null
  key: string
  active: boolean
  has_positive_sample: boolean
  test_url?: string
}

type WorkflowRuntime = {
  phase_modes?: Record<string, number>
  has_model_interpreted_result?: boolean
  has_deterministic_result?: boolean
}

type WorkflowTesting = {
  active_scenario_count?: number
  mocked_endpoint_keys?: string[]
  missing_mock_or_sample_endpoint_keys?: string[]
}

type WorkflowInterrupt = {
  id: string
  purpose?: string
  start_skill?: string
  trigger?: Record<string, unknown>
}

type WorkflowCard = {
  id: string
  purpose?: string
  phase_count?: number
  phases: WorkflowPhase[]
}

type WorkflowPhase = {
  id: string
  purpose?: string
  type: string
  response_mode?: string
  response_renderer?: string
  response_text?: string
  runtime_mode?: string
  runtime_summary?: string
  guidance_only?: boolean
  next?: string
  end_status?: string
  required_fields?: string[]
  optional_fields?: string[]
  required_upload?: Record<string, unknown>
  tools?: string[]
  actions?: WorkflowAction[]
  branches?: WorkflowBranch[]
}

type WorkflowAction = {
  id: string
  purpose?: string
  type: string
  tool?: string
  endpoint?: string
  policy?: string
  matcher?: string
  preconditions_present?: boolean
}

type WorkflowBranch = {
  kind: "when" | "else" | string
  index: number
  purpose?: string
  next?: string
  predicate?: string
}

export function WorkflowYamlEditor({
  value,
  onChange,
  savedValue,
  placeholder,
  minHeight = "420px",
  readOnly = false,
  onSave,
  saveLabel = "Save workflow",
  footerHint = "YAML controls deterministic workflow state, tools, requirements, and responses.",
  outlineSubtitle,
  workflowScope = "skill",
  skillId,
  previewUrl = "/workflow/preview",
}: Props) {
  const dirty = savedValue !== undefined && value !== savedValue
  const editorRef = useRef<EditorViewType | null>(null)
  const lineIndex = useMemo(() => buildWorkflowLineIndex(value), [value])
  const { preview, loading } = useWorkflowPreview({
    value,
    previewUrl,
    workflowScope,
    skillId,
  })

  function jumpToLine(line: number) {
    const view = editorRef.current
    if (!view || line <= 0 || line > view.state.doc.lines) return
    const pos = view.state.doc.line(line).from
    view.dispatch({
      selection: { anchor: pos },
      effects: EditorView.scrollIntoView(pos, { y: "center" }),
    })
    view.focus()
  }

  function jumpTo(kind: WorkflowLineKind, label: string) {
    const line = lineIndex[`${kind}:${label}`]
    if (line) jumpToLine(line)
  }

  function jumpToPath(path?: string) {
    if (!path) return
    const direct = lineIndex[`path:${path}`]
    if (direct) {
      jumpToLine(direct)
      return
    }

    const segment = path.split(".").pop()?.replace(/\[\d+\]/g, "")
    if (!segment) return
    const fallback = Object.entries(lineIndex).find(([key]) => key.includes(`path:`) && key.endsWith(`.${segment}`))?.[1]
    if (fallback) jumpToLine(fallback)
  }

  const graphTitle = "Skill workflow graph"
  const showInspector = true

  return (
    <div className="space-y-4">
      <div className={cn("grid gap-4", showInspector && "xl:grid-cols-[minmax(0,1fr)_430px]")}>
        <div className="space-y-3">
          <YamlCodeEditor
            value={value}
            onChange={onChange}
            minHeight={minHeight}
            placeholder={placeholder}
            readOnly={readOnly}
            onCreateEditor={(view) => {
              editorRef.current = view
            }}
          />
          <footer className="flex flex-wrap items-center justify-between gap-3 text-xs text-app-muted">
            <span>{footerHint}</span>
            {(savedValue !== undefined || onSave) && (
              <div className="flex items-center gap-2">
                {savedValue !== undefined && (
                  dirty ? (
                    <span className="text-status-warning">Unsaved changes</span>
                  ) : (
                    <span className="text-status-success">Saved</span>
                  )
                )}
                {onSave && (
                  <Button type="button" size="sm" onClick={onSave} disabled={readOnly || !dirty}>
                    {saveLabel}
                  </Button>
                )}
              </div>
            )}
          </footer>
        </div>

        {showInspector && (
          <WorkflowInspector
            preview={preview}
            loading={loading}
            outlineSubtitle={outlineSubtitle}
            workflowScope={workflowScope}
            skillId={skillId}
            onJump={jumpTo}
            onJumpPath={jumpToPath}
          />
        )}
      </div>

      {preview?.outline && ((preview.outline.cards?.length ?? 0) > 0 || (preview.outline.interrupts?.length ?? 0) > 0) && (
        <WorkflowGraphPanel
          outline={preview.outline}
          title={graphTitle}
          subtitle="Skill cards, phases, branches, interrupts, and terminal outcomes from the current YAML."
          height={720}
        />
      )}
    </div>
  )
}

function YamlCodeEditor({
  value,
  onChange,
  minHeight,
  placeholder,
  readOnly,
  onCreateEditor,
}: {
  value: string
  onChange: (next: string) => void
  minHeight: string
  placeholder?: string
  readOnly: boolean
  onCreateEditor: (view: EditorViewType) => void
}) {
  const extensions = useMemo(() => [EditorView.lineWrapping], [])

  return (
    <div className="overflow-hidden rounded-control border border-border bg-white/5 transition focus-within:border-brand-red/50">
      <CodeMirror
        value={value}
        onChange={(next) => onChange(next)}
        onCreateEditor={(view) => onCreateEditor(view)}
        extensions={extensions}
        editable={!readOnly}
        readOnly={readOnly}
        minHeight={minHeight}
        placeholder={placeholder}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          highlightActiveLine: !readOnly,
          autocompletion: false,
        }}
        theme="dark"
        className="text-xs [&_.cm-editor]:bg-transparent [&_.cm-gutters]:border-r [&_.cm-gutters]:border-border [&_.cm-gutters]:bg-app-bg/40"
      />
    </div>
  )
}

function WorkflowInspector({
  preview,
  loading,
  outlineSubtitle,
  workflowScope,
  skillId,
  onJump,
  onJumpPath,
}: {
  preview: WorkflowPreview | null
  loading: boolean
  outlineSubtitle?: string
  workflowScope: WorkflowScope
  skillId?: number
  onJump: (kind: WorkflowLineKind, label: string) => void
  onJumpPath: (path?: string) => void
}) {
  const outline = preview?.outline
  const warnings = preview?.warnings ?? []
  const errors = preview?.errors ?? []
  const scopedTitle = "Skill workflow"

  return (
    <aside className="space-y-3 rounded-control border border-border bg-white/[0.03] p-4">
      <div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-app-text">
            <GitBranch size={15} className="text-brand-red" />
            {scopedTitle}
          </div>
          <PreviewBadge preview={preview} loading={loading} />
        </div>
        <div className="mt-1 text-xs text-app-muted">
          {outlineSubtitle ?? scopeHint()}
        </div>
      </div>

      <WorkflowActionStrip outline={outline} skillId={skillId} />
      <IssueList errors={errors} warnings={warnings} onJumpPath={onJumpPath} />

      <div className="max-h-[620px] overflow-auto pr-1">
        <WorkflowSummary outline={outline} workflowScope={workflowScope} onJump={onJump} />
      </div>
    </aside>
  )
}

function PreviewBadge({ preview, loading }: { preview: WorkflowPreview | null; loading: boolean }) {
  if (loading) {
    return <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-app-muted">Checking</span>
  }
  if (!preview) {
    return <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-app-subtle">Local</span>
  }
  if (preview.ok && preview.warnings.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-status-success/30 bg-status-success/10 px-2 py-0.5 text-[11px] text-status-success">
        <CheckCircle2 size={12} />
        Valid
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-status-warning/30 bg-status-warning/10 px-2 py-0.5 text-[11px] text-status-warning">
      <AlertTriangle size={12} />
      Review
    </span>
  )
}

function WorkflowActionStrip({ outline, skillId }: { outline?: WorkflowOutline; skillId?: number }) {
  const endpointSamples = outline?.endpoint_samples ?? []
  const testHref = skillId ? `/skills/${skillId}/test?tab=test` : null
  const phaseModeText = runtimeModeSummary(outline?.runtime)

  if (!outline) {
    return (
      <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-xs text-app-muted">
        Forge compiles this workflow while you type.
      </div>
    )
  }

  return (
    <div className="space-y-2 rounded-control border border-border bg-white/[0.03] px-3 py-2">
      <div className="flex flex-wrap gap-1.5">
        <ActionChip label="Explain workflow" detail="cards below" />
        <ActionChip label="Callable tools" detail="per phase" />
        {testHref && <ActionLink href={testHref} label="Run one scenario" />}
        {testHref && <ActionLink href={testHref} label="Generate mock" />}
        {endpointSamples.map((endpoint) => (
          <ActionLink
            key={endpoint.key}
            href={endpoint.test_url || `/endpoints?endpoint=${encodeURIComponent(endpoint.key)}`}
            label={`Fetch ${endpoint.key}`}
            tone={endpoint.has_positive_sample ? "ok" : "warn"}
          />
        ))}
      </div>
      <div className="text-[11px] text-app-muted">
        Live compile: {phaseModeText}. Text marked as guidance is visible to Ally but not enforced.
      </div>
    </div>
  )
}

function ActionChip({ label, detail }: { label: string; detail: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-app-panel3 px-2 py-1 text-[11px] text-app-muted">
      <span className="font-medium text-app-text">{label}</span>
      <span>{detail}</span>
    </span>
  )
}

function ActionLink({ href, label, tone = "default" }: { href: string; label: string; tone?: "default" | "ok" | "warn" }) {
  return (
    <a
      href={href}
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-1 text-[11px] transition hover:bg-white/10",
        tone === "ok" && "border-status-success/30 bg-status-success/10 text-status-success",
        tone === "warn" && "border-status-warning/30 bg-status-warning/10 text-status-warning",
        tone === "default" && "border-border bg-app-panel3 text-app-text",
      )}
    >
      {label}
    </a>
  )
}

function runtimeModeSummary(runtime?: WorkflowRuntime): string {
  const modes = runtime?.phase_modes ?? {}
  const bits = Object.entries(modes)
    .filter(([, count]) => count > 0)
    .map(([mode, count]) => `${humanizeId(mode)} ${count}`)
  return bits.length > 0 ? bits.join(" · ") : "no executable phases yet"
}

function IssueList({
  errors,
  warnings,
  onJumpPath,
}: {
  errors: string[]
  warnings: PreviewWarning[]
  onJumpPath: (path?: string) => void
}) {
  const hardWarnings = warnings.filter((warning) => warning.severity === "error")
  const softWarnings = warnings.filter((warning) => warning.severity !== "error")
  if (errors.length === 0 && warnings.length === 0) {
    return (
      <div className="rounded-control border border-status-success/20 bg-status-success/5 px-3 py-2 text-xs text-status-success">
        Runtime parser and reference scanner are clean.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {errors.map((error, index) => (
        <div key={`error-${index}`} className="rounded-control border border-status-error/30 bg-status-error/10 px-3 py-2 text-xs text-status-error">
          {error}
        </div>
      ))}
      {[...hardWarnings, ...softWarnings].map((warning, index) => (
        <button
          key={`${warning.code}-${warning.path}-${index}`}
          type="button"
          onClick={() => onJumpPath(warning.path)}
          className={cn(
            "block w-full rounded-control border px-3 py-2 text-left text-xs transition hover:bg-white/5",
            warning.severity === "error" && "border-status-error/30 bg-status-error/10 text-status-error",
            warning.severity === "warning" && "border-status-warning/30 bg-status-warning/10 text-status-warning",
            warning.severity === "info" && "border-border bg-white/[0.03] text-app-muted",
          )}
        >
          <span className="font-medium">{warning.message}</span>
          {warning.path && <span className="mt-1 block text-[11px] opacity-75">{warning.path}</span>}
          {warning.suggestions && warning.suggestions.length > 0 && (
            <span className="mt-2 flex flex-wrap gap-1">
              {warning.suggestions.map((suggestion) => (
                <code key={suggestion} className="rounded-full border border-current/20 px-2 py-0.5 text-[11px]">
                  use {suggestion}
                </code>
              ))}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

function WorkflowSummary({
  outline,
  workflowScope,
  onJump,
}: {
  outline?: WorkflowOutline
  workflowScope: WorkflowScope
  onJump: (kind: WorkflowLineKind, label: string) => void
}) {
  const interrupts = outline?.interrupts ?? []
  const blockedInterrupts = outline?.blocked_interrupts ?? []
  const startActions = outline?.start_actions ?? []
  const cards = outline?.cards ?? []
  const purpose = outline?.purpose?.trim()
  const triggerText = triggerSummary(outline?.trigger)

  if (!purpose && !triggerText && interrupts.length === 0 && blockedInterrupts.length === 0 && startActions.length === 0 && cards.length === 0) {
    return <EmptyInspector message="No human-readable notes yet. Add `purpose` notes to the YAML." />
  }

  return (
    <div className="space-y-4">
      {triggerText && (
        <section className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-app-subtle">Trigger</div>
          <SummaryRow title={triggerText} detail={triggerDetail(outline?.trigger)} />
        </section>
      )}

      {purpose && (
        <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-app-subtle">Purpose</div>
          <div className="mt-1 text-sm text-app-text">{purpose}</div>
        </div>
      )}

      {(interrupts.length > 0 || blockedInterrupts.length > 0) && (
        <section className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-status-warning">
            <ShieldCheck size={13} />
            Interrupts
          </div>
          {interrupts.length === 0 ? (
            <div className="rounded-control border border-border bg-white/[0.03] px-3 py-2 text-xs text-app-muted">
              No interrupts configured.
            </div>
          ) : (
            interrupts.map((interrupt) => (
              <SummaryRow
                key={interrupt.id}
                tone="warning"
                title={interruptSummary(interrupt)}
                detail={interruptDetail(interrupt, blockedInterrupts)}
                onClick={() => onJump("interrupt", interrupt.id)}
              />
            ))
          )}
          {blockedInterrupts.length > 0 && (
            <div className="rounded-control border border-status-warning/20 bg-status-warning/5 px-3 py-2 text-xs text-status-warning">
              Blocks {humanList(blockedInterrupts.map(shortInterruptName))} while this {workflowScope} workflow is active.
            </div>
          )}
        </section>
      )}

      {startActions.length > 0 && (
        <section className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-app-subtle">Before cards</div>
          {startActions.map((action) => (
            <SummaryRow
              key={action.id}
              title={actionSummary(action)}
              detail={actionDetail(action)}
              onClick={() => onJump(action.tool ? "tool" : "phase", action.id)}
            />
          ))}
        </section>
      )}

      {cards.length > 0 && (
        <section className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-app-subtle">Workflow cards</div>
          {cards.map((card) => (
            <div key={card.id} className="space-y-2">
              <SummaryRow
                title={`Card: ${humanizeId(card.id)}`}
                detail={cardDetail(card)}
                onClick={() => onJump("path", "cards")}
              />
              <div className="space-y-2 border-l border-border pl-3">
                {card.phases.map((phase) => (
                  <PhaseCard
                    key={`${card.id}-${phase.id}`}
                    phase={phase}
                    onClick={() => onJump("phase", `${card.id}.${phase.id}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

function PhaseCard({ phase, onClick }: { phase: WorkflowPhase; onClick: () => void }) {
  const badges = [
    phase.runtime_mode ? humanizeId(phase.runtime_mode) : null,
    phase.response_mode ? `response: ${phase.response_mode}` : null,
    phase.response_renderer ? `renderer: ${phase.response_renderer}` : null,
    phase.guidance_only ? "guidance only" : null,
  ].filter(Boolean)

  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded-control border border-border bg-white/[0.03] px-3 py-2 text-left transition hover:bg-white/[0.06]"
    >
      <div className="text-sm font-medium text-app-text">{phaseSummary(phase)}</div>
      {phase.runtime_summary && <div className="mt-1 text-xs text-app-muted">{phase.runtime_summary}</div>}
      {phase.branches && phase.branches.length > 0 && (
        <div className="mt-1 text-xs text-app-muted">{phase.branches.map(branchSummary).join(" · ")}</div>
      )}
      {phase.tools && phase.tools.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {phase.tools.map((tool) => (
            <code key={tool} className="rounded-full border border-border bg-app-panel3 px-2 py-0.5 text-[11px] text-app-text">
              {tool}
            </code>
          ))}
        </div>
      )}
      {badges.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {badges.map((badge) => (
            <span key={badge} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-app-subtle">
              {badge}
            </span>
          ))}
        </div>
      )}
    </button>
  )
}

function SummaryRow({
  title,
  detail,
  tone = "default",
  onClick,
}: {
  title: string
  detail?: string
  tone?: "default" | "warning"
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "block w-full rounded-control border px-3 py-2 text-left transition hover:bg-white/[0.06]",
        tone === "warning" ? "border-status-warning/20 bg-status-warning/5" : "border-border bg-white/[0.03]",
      )}
    >
      <div className={cn("text-sm font-medium", tone === "warning" ? "text-status-warning" : "text-app-text")}>{title}</div>
      {detail && <div className="mt-1 text-xs text-app-muted">{detail}</div>}
    </button>
  )
}

function EmptyInspector({ message }: { message: string }) {
  return (
    <div className="rounded-control border border-dashed border-border px-3 py-4 text-xs text-app-muted">
      {message}
    </div>
  )
}

function triggerSummary(trigger?: Record<string, unknown>): string {
  if (!trigger || Object.keys(trigger).length === 0) return ""
  const intent = String(trigger.intent ?? "").trim()
  const description = String(trigger.description ?? "").trim()
  if (intent) return `User intent: ${humanizeId(intent)}`
  if (description) return description
  const type = String(trigger.type ?? "").trim()
  return type ? `Trigger: ${humanizeId(type)}` : ""
}

function triggerDetail(trigger?: Record<string, unknown>): string {
  if (!trigger) return ""
  const keywords = Array.isArray(trigger.keywords) ? trigger.keywords.map(String).filter(Boolean).slice(0, 4) : []
  const examples = Array.isArray(trigger.examples) ? trigger.examples.map(String).filter(Boolean).slice(0, 2) : []
  const bits = [
    keywords.length > 0 ? `keywords: ${keywords.join(", ")}` : null,
    examples.length > 0 ? `examples: ${examples.join(" / ")}` : null,
  ].filter(Boolean)
  return bits.join(" · ")
}

function actionSummary(action: WorkflowAction): string {
  if (action.endpoint) return `Fetch ${action.endpoint}`
  if (action.tool) return `Call ${action.tool}`
  if (action.policy) return `Evaluate ${action.policy}`
  if (action.matcher) return `Match with ${action.matcher}`
  return humanizeId(action.id || action.type)
}

function actionDetail(action: WorkflowAction): string {
  const bits = [
    action.purpose || null,
    action.preconditions_present ? "has preconditions" : null,
    action.type ? `type: ${action.type}` : null,
  ].filter(Boolean)
  return bits.join(" · ")
}

function cardDetail(card: WorkflowCard): string {
  const bits = [
    card.purpose || null,
    `${card.phase_count ?? card.phases.length} phases`,
  ].filter(Boolean)
  return bits.join(" · ")
}

function phaseSummary(phase: WorkflowPhase): string {
  if (phase.type === "collect_fields") return `Ask for ${humanList([...(phase.required_fields ?? []), ...(phase.optional_fields ?? [])]) || "fields"}`
  if (phase.type === "collect_upload") return "Ask for an upload"
  if (phase.type === "branch") return `Decide next path: ${humanizeId(phase.id)}`
  if (phase.type === "action") return actionPhaseSummary(phase)
  if (phase.type === "emit") return phase.response_text ? `Say "${truncate(phase.response_text, 64)}"` : `Say ${humanizeId(phase.id)}`
  if (phase.type === "end") return `End as ${phase.end_status || humanizeId(phase.id)}`
  return humanizeId(phase.id || phase.type)
}

function actionPhaseSummary(phase: WorkflowPhase): string {
  const endpoints = (phase.actions ?? []).map((action) => action.endpoint).filter((value): value is string => Boolean(value))
  const tools = (phase.tools ?? []).filter(Boolean)
  const names = [...endpoints, ...tools]
  return names.length > 0 ? `Run ${humanList(names)}` : `Run ${humanizeId(phase.id)}`
}

function branchSummary(branch: WorkflowBranch): string {
  const target = branch.next ? `-> ${humanizeId(branch.next)}` : ""
  const predicate = branch.predicate ? `${branch.predicate} ` : ""
  return `${predicate}${target}`.trim()
}

function interruptSummary(interrupt: WorkflowInterrupt): string {
  const trigger = interrupt.trigger ?? {}
  const tool = String(trigger.tool ?? "")
  const subject =
    tool === "detect_human_request" ? "user asks for human"
    : tool === "detect_frustration" ? frustrationSubject(trigger)
    : trigger.type === "phrase" || trigger.type === "phrases" ? phraseSubject(trigger)
    : trigger.when ? "workflow condition matches"
    : humanizeId(interrupt.id)
  const target = targetLabel(interrupt)
  return target ? `${subject} -> ${target}` : subject
}

function interruptDetail(interrupt: WorkflowInterrupt, blockedInterrupts: string[]): string {
  const pieces: string[] = []
  const trigger = interrupt.trigger ?? {}
  const tool = String(trigger.tool ?? "")
  if (interrupt.purpose) pieces.push(interrupt.purpose)
  if (tool) pieces.push(`uses ${tool}`)

  const conditions = isRecord(trigger.conditions) ? trigger.conditions : {}
  if (conditions.severity_gte) pieces.push(`severity >= ${String(conditions.severity_gte)}`)
  if (conditions.outcome) pieces.push(`outcome ${String(conditions.outcome)}`)
  if (isBlockedInterrupt(interrupt.id, blockedInterrupts)) pieces.push("unless blocked")
  return pieces.join(" · ")
}

function frustrationSubject(trigger: Record<string, unknown>): string {
  const conditions = isRecord(trigger.conditions) ? trigger.conditions : {}
  return conditions.severity_gte === "severe" ? "user severely frustrated" : "user frustrated"
}

function phraseSubject(trigger: Record<string, unknown>): string {
  const phrases = [
    trigger.phrase,
    trigger.phrases,
    trigger.any_phrase,
    trigger.any_phrases,
    trigger.keywords,
    trigger.examples,
  ].flat().filter((value): value is string => typeof value === "string" && value.trim().length > 0)
  if (phrases.length === 0) return "user phrase matches"
  return `user says "${phrases[0]}"`
}

function targetLabel(interrupt: WorkflowInterrupt): string {
  const raw = interrupt.start_skill || ""
  if (!raw) return ""
  const label = humanizeId(raw)
  if (raw.toLowerCase().includes("escalat")) return "escalate to human"
  return `go to ${label}`
}

function isBlockedInterrupt(id: string, blockedInterrupts: string[]): boolean {
  return blockedInterrupts.includes(id) || blockedInterrupts.includes(`agent.${id}`)
}

function shortInterruptName(value: string): string {
  return humanizeId(value.replace(/^agent\./, ""))
}

function humanList(values: string[]): string {
  const clean = values.filter(Boolean)
  if (clean.length <= 1) return clean.join("")
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`
  return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value
}

function humanizeId(value?: string): string {
  return String(value ?? "")
    .replace(/^agent\./, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function scopeHint(): string {
  return "Score Card prompt, steps, fields, data loaders, policy gates, and deterministic actions."
}

function useWorkflowPreview({
  value,
  previewUrl,
  workflowScope,
  skillId,
}: {
  value: string
  previewUrl: string
  workflowScope: WorkflowScope
  skillId?: number
}) {
  const [preview, setPreview] = useState<WorkflowPreview | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (value.trim() === "") {
      setPreview(null)
      setLoading(false)
      return
    }

    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      setLoading(true)
      fetch(previewUrl, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({
          workflow_yaml: value,
          workflow_scope: workflowScope,
          skill_id: skillId,
        }),
      })
        .then(async (res) => {
          const json = await res.json()
          if (!res.ok) throw new Error(json?.error ?? `Preview failed with ${res.status}`)
          setPreview(normalizePreview(json))
        })
        .catch((error: Error) => {
          if (error.name === "AbortError") return
          setPreview({
            ok: false,
            errors: [error.message],
            warnings: [],
            outline: { cards: [], interrupts: [], all_tools: [] },
          })
        })
        .finally(() => setLoading(false))
    }, 350)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [previewUrl, skillId, value, workflowScope])

  return { preview, loading }
}

function normalizePreview(raw: unknown): WorkflowPreview {
  const h = isRecord(raw) ? raw : {}
  return {
    ok: h.ok === true,
    errors: Array.isArray(h.errors) ? h.errors.map(String) : [],
    warnings: Array.isArray(h.warnings) ? h.warnings.map(normalizeWarning) : [],
    outline: normalizeOutline(h.outline),
  }
}

function normalizeWarning(raw: unknown): PreviewWarning {
  const h = isRecord(raw) ? raw : {}
  const severity = h.severity === "error" || h.severity === "warning" || h.severity === "info" ? h.severity : "info"
  return {
    code: String(h.code ?? "workflow_warning"),
    severity,
    message: String(h.message ?? "Workflow warning"),
    path: h.path === undefined ? undefined : String(h.path),
    value: h.value === undefined ? undefined : String(h.value),
    suggestions: Array.isArray(h.suggestions) ? h.suggestions.map(String) : [],
  }
}

function normalizeOutline(raw: unknown): WorkflowOutline {
  const h = isRecord(raw) ? raw : {}
  return {
    engine: h.engine === undefined ? undefined : String(h.engine),
    version: h.version as WorkflowOutline["version"],
    purpose: h.purpose === undefined ? undefined : String(h.purpose),
    trigger: isRecord(h.trigger) ? h.trigger : {},
    scope: typeof h.scope === "string" ? h.scope : undefined,
    global_tools: Array.isArray(h.global_tools) ? h.global_tools.map(String) : [],
    all_tools: Array.isArray(h.all_tools) ? h.all_tools.map(String) : [],
    blocked_interrupts: Array.isArray(h.blocked_interrupts) ? h.blocked_interrupts.map(String) : [],
    endpoint_allowlists: normalizeEndpointAllowlists(h.endpoint_allowlists),
    endpoint_samples: Array.isArray(h.endpoint_samples) ? h.endpoint_samples.map(normalizeEndpointSample) : [],
    runtime: normalizeRuntime(h.runtime),
    testing: normalizeTesting(h.testing),
    start_actions: Array.isArray(h.start_actions) ? h.start_actions.map(normalizeAction) : [],
    interrupts: Array.isArray(h.interrupts) ? h.interrupts.map(normalizeInterrupt) : [],
    cards: Array.isArray(h.cards) ? h.cards.map(normalizeCard) : [],
  }
}

function normalizeEndpointAllowlists(raw: unknown): WorkflowOutline["endpoint_allowlists"] {
  const h = isRecord(raw) ? raw : {}
  const phases = isRecord(h.phases)
    ? Object.fromEntries(Object.entries(h.phases).map(([key, value]) => [key, Array.isArray(value) ? value.map(String) : []]))
    : {}
  return {
    global: Array.isArray(h.global) ? h.global.map(String) : [],
    phases,
  }
}

function normalizeEndpointSample(raw: unknown): WorkflowEndpointSample {
  const h = isRecord(raw) ? raw : {}
  return {
    id: Number.isFinite(Number(h.id)) ? Number(h.id) : null,
    key: String(h.key ?? ""),
    active: h.active === true,
    has_positive_sample: h.has_positive_sample === true,
    test_url: h.test_url === undefined ? undefined : String(h.test_url),
  }
}

function normalizeRuntime(raw: unknown): WorkflowRuntime {
  const h = isRecord(raw) ? raw : {}
  return {
    phase_modes: isRecord(h.phase_modes)
      ? Object.fromEntries(Object.entries(h.phase_modes).map(([key, value]) => [key, Number(value ?? 0)]))
      : {},
    has_model_interpreted_result: h.has_model_interpreted_result === true,
    has_deterministic_result: h.has_deterministic_result === true,
  }
}

function normalizeTesting(raw: unknown): WorkflowTesting {
  const h = isRecord(raw) ? raw : {}
  return {
    active_scenario_count: Number(h.active_scenario_count ?? 0),
    mocked_endpoint_keys: Array.isArray(h.mocked_endpoint_keys) ? h.mocked_endpoint_keys.map(String) : [],
    missing_mock_or_sample_endpoint_keys: Array.isArray(h.missing_mock_or_sample_endpoint_keys) ? h.missing_mock_or_sample_endpoint_keys.map(String) : [],
  }
}

function normalizeInterrupt(raw: unknown): WorkflowInterrupt {
  const h = isRecord(raw) ? raw : {}
  return {
    id: String(h.id ?? ""),
    purpose: h.purpose === undefined ? undefined : String(h.purpose),
    start_skill: h.start_skill === undefined ? undefined : String(h.start_skill),
    trigger: isRecord(h.trigger) ? h.trigger : {},
  }
}

function normalizeCard(raw: unknown): WorkflowCard {
  const h = isRecord(raw) ? raw : {}
  return {
    id: String(h.id ?? ""),
    purpose: optionalString(h.purpose),
    phase_count: Number(h.phase_count ?? 0),
    phases: Array.isArray(h.phases) ? h.phases.map(normalizePhase) : [],
  }
}

function normalizePhase(raw: unknown): WorkflowPhase {
  const h = isRecord(raw) ? raw : {}
  return {
    id: String(h.id ?? ""),
    purpose: optionalString(h.purpose),
    type: String(h.type ?? ""),
    response_mode: optionalString(h.response_mode),
    response_renderer: optionalString(h.response_renderer),
    response_text: optionalString(h.response_text),
    runtime_mode: optionalString(h.runtime_mode),
    runtime_summary: optionalString(h.runtime_summary),
    guidance_only: h.guidance_only === true,
    next: optionalString(h.next),
    end_status: optionalString(h.end_status),
    required_fields: Array.isArray(h.required_fields) ? h.required_fields.map(String) : [],
    optional_fields: Array.isArray(h.optional_fields) ? h.optional_fields.map(String) : [],
    required_upload: isRecord(h.required_upload) ? h.required_upload : {},
    tools: Array.isArray(h.tools) ? h.tools.map(String) : [],
    actions: Array.isArray(h.actions) ? h.actions.map(normalizeAction) : [],
    branches: Array.isArray(h.branches) ? h.branches.map(normalizeBranch) : [],
  }
}

function normalizeAction(raw: unknown): WorkflowAction {
  const h = isRecord(raw) ? raw : {}
  return {
    id: String(h.id ?? ""),
    purpose: optionalString(h.purpose),
    type: String(h.type ?? ""),
    tool: optionalString(h.tool),
    endpoint: optionalString(h.endpoint),
    policy: optionalString(h.policy),
    matcher: optionalString(h.matcher),
    preconditions_present: h.preconditions_present === true,
  }
}

function normalizeBranch(raw: unknown): WorkflowBranch {
  const h = isRecord(raw) ? raw : {}
  return {
    kind: String(h.kind ?? "when"),
    index: Number(h.index ?? 0),
    purpose: optionalString(h.purpose),
    next: optionalString(h.next),
    predicate: optionalString(h.predicate),
  }
}

function optionalString(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  const text = String(value).trim()
  return text.length > 0 ? text : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function csrfToken(): string {
  const el = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")
  return el?.content ?? ""
}

type WorkflowLineKind = "phase" | "interrupt" | "tool" | "branch" | "path"

type WorkflowOutlineItem = {
  kind: "phase" | "interrupt" | "tool" | "branch"
  label: string
  indent: number
  meta?: string
  line?: number
}

type WorkflowLineIndex = Record<string, number>

export function workflowOutline(yaml: string): WorkflowOutlineItem[] {
  const parsed = scanWorkflowYaml(yaml)
  return parsed.items
}

function buildWorkflowLineIndex(yaml: string): WorkflowLineIndex {
  return scanWorkflowYaml(yaml).lineIndex
}

function scanWorkflowYaml(yaml: string): { items: WorkflowOutlineItem[]; lineIndex: WorkflowLineIndex } {
  const contexts: Array<{ indent: number; name: string }> = []
  const items: WorkflowOutlineItem[] = []
  const lineIndex: WorkflowLineIndex = {}

  yaml.split("\n").forEach((line, zeroIndex) => {
    const lineNumber = zeroIndex + 1
    const indent = line.match(/^\s*/)?.[0].length ?? 0
    const trimmed = line.trim()
    if (trimmed === "" || trimmed.startsWith("#")) return

    while (contexts.length > 0 && indent <= contexts[contexts.length - 1].indent) contexts.pop()

    indexPathCandidate(lineIndex, trimmed, lineNumber)

    const keyMatch = trimmed.match(/^([a-zA-Z0-9_]+):/)
    if (keyMatch) {
      const key = keyMatch[1]
      lineIndex[`path:${key}`] ||= lineNumber
      if (["phases", "steps", "interrupts", "allowed_tool_calls", "branches"].includes(key)) {
        contexts.push({ indent, name: key })
      }
    }

    const context = contexts[contexts.length - 1]?.name
    const idMatch = trimmed.match(/^-\s*id:\s*(.+)$/)
    if (idMatch) {
      const label = idMatch[1].replace(/^["']|["']$/g, "")
      if (context === "phases" || context === "steps") {
        items.push({ kind: "phase", label, indent: 1, line: lineNumber })
        lineIndex[`phase:${label}`] = lineNumber
      }
      if (context === "interrupts") {
        items.push({ kind: "interrupt", label, indent: 0, line: lineNumber })
        lineIndex[`interrupt:${label}`] = lineNumber
      }
    }

    if (context === "allowed_tool_calls") {
      const toolMatch = trimmed.match(/^([a-zA-Z0-9_]+):/)
      if (toolMatch && toolMatch[1] !== "allowed_tool_calls") {
        items.push({ kind: "tool", label: toolMatch[1], indent: 1, line: lineNumber })
        lineIndex[`tool:${toolMatch[1]}`] = lineNumber
      }
    }

    if (context === "branches" && /^-\s*(when|else):/.test(trimmed)) {
      const label = trimmed.replace(/^-\s*/, "")
      items.push({ kind: "branch", label, indent: 2, line: lineNumber })
      lineIndex[`branch:${label}`] = lineNumber
    }
  })

  return { items, lineIndex }
}

function indexPathCandidate(lineIndex: WorkflowLineIndex, trimmed: string, lineNumber: number) {
  const keyMatch = trimmed.match(/^(?:-\s*)?([a-zA-Z0-9_]+):/)
  if (!keyMatch) return
  const key = keyMatch[1]
  lineIndex[`path:${key}`] ||= lineNumber
}
